"use client"
import { HudExtrapolatedGraphBlock, useHudContext } from "../../shared/playbookHUDInfra"
import type {
  ExtrapolatedGraphScaleOverrides,
} from "../../../extrapolated-graph/types"
import type { RiskFoomDesignId, RiskFoomPreviewMode, RiskAmbientDesignId, RiskSpaceExplorerMode, RiskTedGuideMode } from "../../../extrapolated-graph/RiskFoomPreviewOverlay"

// Plot-area left padding 0 starts the takeover-risk dither (and the
// start-pinned current-year tick) flush with the HUD body's left edge, so the
// graph left-aligns with the "Current Policy" label in the panel below it.
// Module-level (not inline) so the memoized graph keeps a stable reference.
const HUD_GRAPH_SCALE_OVERRIDES: ExtrapolatedGraphScaleOverrides = {
  padding: { left: 0 },
}

// The standard PRIMARY_GRAPH_HEIGHT_PX-tall extrapolated graph used by both v7
// and v8. Hover state, plan, dates, etc. all come from context. In epilogue
// mode the height shrinks: ctx.epilogue.graphHeight is already tweened (same
// duration/easing as the graph's slide-down), so it's rendered as-is.
// `paused` freezes per-frame date updates while the graph is faded out behind
// a metric-details state.
// The rest are per-variant restyle knobs (see the HUD 87 usage in
// variants.tsx): year pill instead of the timeline year tick, dropped
// AI CAPABILITIES / TRAILING ACTOR RISK labels, recolored solid line segment
// with an endpoint dot at the solid→dashed transition.
export const PrimaryGraph = ({
  currentYearPill,
  onCurrentYearPillClick,
  currentYearPillMenuOpen,
  showAICapabilitiesLabel,
  capabilityPastLineColor,
  capabilityPastLineStrokeWidth,
  clipLinesToDitherTop,
  capabilityLineFloorValue,
  showCurrentPointMarker,
  currentPointMarkerRadius,
  showCurrentPointCapabilityLabel,
  minimumGraphDate,
  hideGraphExplainerTooltip,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover,
  riskFoomPreview,
  riskFoomDesign,
  riskFoomTedCaption,
  riskFoomOnPointHover,
  riskSpaceExplorer,
  riskTedGuide,
  riskAmbientDisplay,
  riskHoverDimDisabled,
  preChoiceYAxisMax,
  graphLogScale,
  highlightLineWhilePointHovered,
  riskZoneDimTween,
  riskZoneDimInverted,
  riskZoneDimDotShrink,
  riskZoneContinuousGuide,
  pointGuideTedDash,
  paused,
}: {
  currentYearPill?: boolean
  // See ExtrapolatedGraphRenderProps.onCurrentYearPillClick — the HUD's
  // year-jump menu toggle riding the year pill.
  onCurrentYearPillClick?: () => void
  currentYearPillMenuOpen?: boolean
  showAICapabilitiesLabel?: boolean
  capabilityPastLineColor?: string
  capabilityPastLineStrokeWidth?: number
  // Clips the capability line to the red dither field's top edge (tab 49) —
  // see clipLinesToDitherTop in extrapolated-graph/types.ts.
  clipLinesToDitherTop?: boolean
  capabilityLineFloorValue?: number
  showCurrentPointMarker?: boolean
  currentPointMarkerRadius?: number
  showCurrentPointCapabilityLabel?: boolean
  // Floor for the graph's date — see HudExtrapolatedGraphBlock. Keeps some
  // of the capability line visible at the very top of the article, where
  // the page's first data-chart-date marker rewinds the HUD to 2025.
  minimumGraphDate?: Date
  // See PlaybookExtrapolatedGraph's hideGraphExplainerTooltip prop.
  hideGraphExplainerTooltip?: boolean
  // See ExtrapolatedGraphRenderProps.pointHoverMaxDistanceToLinePx.
  pointHoverMaxDistanceToLinePx?: number
  // See ExtrapolatedGraphRenderProps.takeoverRiskAreaHover.
  takeoverRiskAreaHover?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomPreview.
  riskFoomPreview?: RiskFoomPreviewMode
  // See ExtrapolatedGraphRenderProps.riskFoomDesign.
  riskFoomDesign?: RiskFoomDesignId
  // See ExtrapolatedGraphRenderProps.riskFoomTedCaption.
  riskFoomTedCaption?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomOnPointHover.
  riskFoomOnPointHover?: boolean
  // See ExtrapolatedGraphProps.riskSpaceExplorer (the 2D risk-space explorer).
  riskSpaceExplorer?: RiskSpaceExplorerMode
  // See ExtrapolatedGraphProps.riskTedGuide (the tab-26 TED guide).
  riskTedGuide?: RiskTedGuideMode
  // See ExtrapolatedGraphProps.riskAmbientDisplay (the always-on readout).
  riskAmbientDisplay?: RiskAmbientDesignId
  // See ExtrapolatedGraphProps.riskHoverDimDisabled (tab 53: no field dim).
  riskHoverDimDisabled?: boolean
  // See PlaybookExtrapolatedGraph.preChoiceYAxisMax (tab 33 close-up zoom).
  preChoiceYAxisMax?: number
  // See PlaybookExtrapolatedGraph.useLogScale (tab 33 pairs it with the
  // zoomed-out close-up so the line keeps a readable curve).
  graphLogScale?: boolean
  // See ExtrapolatedGraphRenderProps.highlightLineWhilePointHovered.
  highlightLineWhilePointHovered?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimTween (tabs 66–70).
  riskZoneDimTween?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimInverted (tab 70).
  riskZoneDimInverted?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimDotShrink (picks tab 2).
  riskZoneDimDotShrink?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneContinuousGuide (tab 68).
  riskZoneContinuousGuide?: boolean
  // See ExtrapolatedGraphRenderProps.pointGuideTedDash.
  pointGuideTedDash?: boolean
  paused?: boolean
} = {}) => {
  const ctx = useHudContext()

  return (
    <HudExtrapolatedGraphBlock
      height={ctx.epilogue.graphHeight}
      numCapabilityThresholdLabels={0}
      redAboveLossOfControl
      currentYearPill={currentYearPill}
      onCurrentYearPillClick={onCurrentYearPillClick}
      currentYearPillMenuOpen={currentYearPillMenuOpen}
      showAICapabilitiesLabel={showAICapabilitiesLabel}
      capabilityPastLineColor={capabilityPastLineColor}
      capabilityPastLineStrokeWidth={capabilityPastLineStrokeWidth}
      clipLinesToDitherTop={clipLinesToDitherTop}
      capabilityLineFloorValue={capabilityLineFloorValue}
      showCurrentPointMarker={showCurrentPointMarker}
      currentPointMarkerRadius={currentPointMarkerRadius}
      showCurrentPointCapabilityLabel={showCurrentPointCapabilityLabel}
      minimumGraphDate={minimumGraphDate}
      hideGraphExplainerTooltip={hideGraphExplainerTooltip}
      pointHoverMaxDistanceToLinePx={pointHoverMaxDistanceToLinePx}
      takeoverRiskAreaHover={takeoverRiskAreaHover}
      riskFoomPreview={riskFoomPreview}
      riskFoomDesign={riskFoomDesign}
      riskFoomTedCaption={riskFoomTedCaption}
      riskFoomOnPointHover={riskFoomOnPointHover}
      riskSpaceExplorer={riskSpaceExplorer}
      riskTedGuide={riskTedGuide}
      riskAmbientDisplay={riskAmbientDisplay}
      riskHoverDimDisabled={riskHoverDimDisabled}
      preChoiceYAxisMax={preChoiceYAxisMax}
      graphLogScale={graphLogScale}
      highlightLineWhilePointHovered={highlightLineWhilePointHovered}
      riskZoneDimTween={riskZoneDimTween}
      riskZoneDimInverted={riskZoneDimInverted}
      riskZoneDimDotShrink={riskZoneDimDotShrink}
      riskZoneContinuousGuide={riskZoneContinuousGuide}
      pointGuideTedDash={pointGuideTedDash}
      scaleOverrides={HUD_GRAPH_SCALE_OVERRIDES}
      // While no plan is chosen yet, show the trailing-actor-risk gradient.
      blackBelowTooSlowRisk={ctx.selectedPlan === null}
      showAllYears={false}
      showYearDots={false}
      showRiskBackgroundLabels={ctx.isHudHovered}
      paused={paused}
    />
  )
}
